/** @format */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
	Box,
	Button,
	DialogContent,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import Scrollbar from "../../scrollbar/Scrollbar";
import { ViewAndSearchAddRequestSendOutTable } from "../../allComponents/AllComponents";
import { BootstrapDialog } from "../notification-modal/styles";
import { useDispatch, useSelector } from "react-redux";
import {
	selectCircleError,
	selectIsLoading,
	selectViewSendAddRequest,
} from "../../../redux/selectors/circleSelector";
import { AppDispatch } from "../../../redux/store/store";
import { fetchViewAndSearchAddRequestSendOut } from "../../../redux/actions/circleAction";
import {
	AddRequestType,
	EmptyViewAndSearchAddRequestSendOutQueryPayload,
	SearchUserAddRequestResult,
	ViewAndSearchAddRequestSendOutQueryPayload,
} from "../../../utility/types/addRequestType";
import { applyFilter2 } from "../../../utility/utils/utils";
import { format } from "date-fns";
import { buttonColors, colors, fonts } from "../../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import { Form, Formik } from "formik";
import { validateViewAndSearchAddRequestSendOut } from "../../../utility/validation/searchUserToSendAddRequestValidation";
import { isNewlyAdded } from "../../../utility/generalUtils";

interface ViewAndSearchAddRequestSendOutProps {
	open: boolean;
	handleClose: () => void;
	circleId: string;
}

const ViewAndSearchAddRequestSendOut = (
	props: ViewAndSearchAddRequestSendOutProps,
) => {
	const { open, handleClose, circleId } = props;

	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(selectIsLoading);

	const addRequestsData: SearchUserAddRequestResult | null = useSelector(
		selectViewSendAddRequest,
	);
	const [error, setError] = useState("");

	const [searchParams] = useState<ViewAndSearchAddRequestSendOutQueryPayload>();

	const [page, setPage] = useState(0);

	const [rowsPerPage, setRowsPerPage] = useState(5);

	useEffect(() => {
		if (open && circleId) {
			dispatch(
				fetchViewAndSearchAddRequestSendOut({
					unionId: circleId,
					searchValues: searchParams,
				}),
			);
		}
	}, [open, circleId, dispatch, searchParams]);

	const addRequests: AddRequestType[] = addRequestsData?.addRequests || [];

	const sortAddRequests = addRequests
		.slice()
		.sort(
			(a, b) =>
				new Date(b.created_At).getTime() - new Date(a.created_At).getTime(),
		);

	const dataFiltered = applyFilter2({
		inputData: addRequests || [],
	});

	const ROW_HEIGHT = 65;
	const HEADER_HEIGHT = 50;
	const MIN_TABLE_HEIGHT = 150;

	// Calculate actual rows in the current page
	const actualRowsCurrentPage = Math.min(
		addRequests.length - page * rowsPerPage,
		rowsPerPage,
	);

	// Calculate the new height
	const calculateTableHeight = () => {
		const rowsHeight = actualRowsCurrentPage * ROW_HEIGHT;
		const totalHeight = rowsHeight + HEADER_HEIGHT; // include header height
		return Math.max(totalHeight, MIN_TABLE_HEIGHT); // ensure it's not smaller than MIN_TABLE_HEIGHT
	};

	// Updates table height when page, rowsPerPage, or combinedData changes
	useEffect(() => {
		const newHeight = calculateTableHeight();
		setTableHeight(newHeight);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, rowsPerPage, addRequests]);

	const initialHeight = Math.min(dataFiltered.length * ROW_HEIGHT, 500);

	const [tableHeight, setTableHeight] = useState(initialHeight);

	const handleChangePage = (event: any, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: any) => {
		setPage(0);
		setRowsPerPage(parseInt(event.target.value, 10));
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return format(date, "PPPpp");
	};

	const initialValues: ViewAndSearchAddRequestSendOutQueryPayload =
		EmptyViewAndSearchAddRequestSendOutQueryPayload;

	const handleSearchSubmit = async (
		values: ViewAndSearchAddRequestSendOutQueryPayload,
		{ resetForm }: any,
	) => {
		try {
			const response = await dispatch(
				fetchViewAndSearchAddRequestSendOut({
					unionId: circleId,
					searchValues: values,
				}),
			);
			if (response.payload.message) {
				setError("");
				resetForm();
			} else {
				setError("Data Not Found.");
				resetForm();
			}
		} catch (error: any) {
			setError("Data Not Found.");
			resetForm();
		}
	};

	return (
		<>
			<BootstrapDialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
				{/* ── Header band ──────────────────────────────────────────── */}
				<Box
					sx={{
						bgcolor: colors.primary,
						px: { xs: 2.5, sm: 4 },
						py: 2.5,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box
							sx={{
								width: 40,
								height: 40,
								borderRadius: "50%",
								bgcolor: alpha(colors.mainSecondary, 0.15),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
							}}
						>
							<SendIcon sx={{ color: colors.mainSecondary, fontSize: 22 }} />
						</Box>
						<Box>
							<Typography
								sx={{
									fontFamily: fonts.montserrat,
									fontWeight: 800,
									fontSize: { xs: "1rem", sm: "1.15rem" },
									color: "#fff",
									lineHeight: 1.2,
								}}
							>
								Add Requests Sent Out
							</Typography>
							<Typography
								sx={{
									fontFamily: fonts.montserrat,
									fontSize: "0.72rem",
									color: alpha("#fff", 0.6),
									mt: 0.3,
								}}
							>
								View and search all add requests you have sent
							</Typography>
						</Box>
					</Stack>
					<IconButton
						aria-label="close"
						onClick={handleClose}
						sx={{ color: alpha("#fff", 0.7) }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<DialogContent sx={{ pt: 3, pb: 2, px: { xs: 2.5, sm: 4 } }}>
					{isLoading ? (
						<LoadingSpinner size={50} loading={isLoading} />
					) : (
						<>
							{(error || addRequests.length) === 0 ? (
								/* Empty state */
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										py: 6,
										gap: 1.5,
									}}
								>
									<SendIcon
										sx={{ fontSize: 48, color: alpha(colors.primary, 0.15) }}
									/>
									<Typography
										sx={{
											fontFamily: fonts.montserrat,
											fontWeight: 600,
											fontSize: "0.9rem",
											color: alpha(colors.primary, 0.45),
										}}
									>
										You have not sent any add requests yet.
									</Typography>
								</Box>
							) : (
								<>
									{/* ── Search form ───────────────────────────────────────── */}
									<Formik
										initialValues={initialValues}
										validationSchema={validateViewAndSearchAddRequestSendOut}
										onSubmit={handleSearchSubmit}
									>
										{({ errors, touched, values, handleChange }) => (
											<Form>
												<Grid container spacing={2} alignItems="flex-end">
													<Grid item xs={12} sm={6} md={3}>
														<TextField
															id="firstName"
															label="First Name"
															variant="outlined"
															size="small"
															name="firstName"
															autoFocus
															fullWidth
															value={values.firstName}
															onChange={handleChange}
															error={!!(errors?.firstName && touched?.firstName)}
															helperText={
																errors.firstName &&
																touched.firstName &&
																errors.firstName
															}
														/>
													</Grid>
													<Grid item xs={12} sm={6} md={3}>
														<TextField
															id="lastName"
															label="Last Name"
															name="lastName"
															variant="outlined"
															size="small"
															fullWidth
															value={values.lastName}
															onChange={handleChange}
															error={!!(errors?.lastName && touched?.lastName)}
															helperText={
																errors.lastName &&
																touched.lastName &&
																errors.lastName
															}
														/>
													</Grid>
													<Grid item xs={12} sm={6} md={2}>
														<TextField
															id="email"
															label="Email"
															name="email"
															variant="outlined"
															size="small"
															fullWidth
															value={values.email}
															onChange={handleChange}
															error={!!(errors?.email && touched?.email)}
															helperText={
																errors.email && touched.email && errors.email
															}
														/>
													</Grid>
													<Grid item xs={12} sm={6} md={2}>
														<FormControl fullWidth size="small">
															<InputLabel id="status-label">Status</InputLabel>
															<Select
																name="status"
																labelId="status-label"
																id="status-select"
																value={values.status}
																label="Status"
																onChange={handleChange}
															>
																<MenuItem value="Pending">Pending</MenuItem>
																<MenuItem value="Approved">Approved</MenuItem>
																<MenuItem value="Rejected">Rejected</MenuItem>
															</Select>
														</FormControl>
													</Grid>
													<Grid item xs={12} sm={6} md={2}>
														<Button
															type="submit"
															variant="contained"
															fullWidth
															sx={{
																bgcolor: colors.mainSecondary,
																color: colors.primary,
																fontFamily: fonts.montserrat,
																fontWeight: 800,
																textTransform: "none",
																borderRadius: 2,
																boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
																"&:hover": { bgcolor: colors.mainSecondary },
															}}
														>
															Search
														</Button>
													</Grid>
												</Grid>
											</Form>
										)}
									</Formik>

									<Divider sx={{ mt: 3, mb: 2 }} />

									{/* ── Error or Table ───────────────────────────────────── */}
									{error ? (
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
												justifyContent: "center",
												py: 4,
												gap: 1,
											}}
										>
											<SendIcon
												sx={{ fontSize: 40, color: alpha(colors.primary, 0.18) }}
											/>
											<Typography
												sx={{
													fontFamily: fonts.montserrat,
													fontWeight: 600,
													fontSize: "0.88rem",
													color: alpha(colors.primary, 0.45),
												}}
											>
												No add requests found matching your search criteria.
											</Typography>
										</Box>
									) : (
										<>
											<Scrollbar sx={{ height: tableHeight }}>
												<TableContainer sx={{ overflow: "auto" }}>
													<Table sx={{ minWidth: 650 }}>
														<TableHead>
															<TableRow sx={{ bgcolor: alpha(colors.primary, 0.04) }}>
																{["Member", "Full Name", "Email", "Status", "Sent On"].map(
																	(h) => (
																		<TableCell
																			key={h}
																			sx={{
																				fontFamily: fonts.montserrat,
																				fontWeight: 700,
																				fontSize: "0.72rem",
																				color: colors.darkGrey,
																				textTransform: "uppercase",
																				letterSpacing: "0.06em",
																				py: 1.5,
																			}}
																		>
																			{h}
																		</TableCell>
																	)
																)}
															</TableRow>
														</TableHead>
														<TableBody>
															{sortAddRequests
																.slice(
																	page * rowsPerPage,
																	page * rowsPerPage + rowsPerPage,
																)
																.map((request: any, index) => (
																	<ViewAndSearchAddRequestSendOutTable
																		key={index}
																		fullName={`${request.member.firstName} ${request.member.lastName}`}
																		email={request.member.email}
																		status={request.status}
																		profileImage={request.member.profileImage}
																		sendDay={formatDate(request.created_At)}
																		isNew={isNewlyAdded(request.created_At)}
																	/>
																))}
														</TableBody>
													</Table>
												</TableContainer>
											</Scrollbar>
											<Divider />
											<TablePagination
												page={page}
												component="div"
												count={addRequests.length}
												rowsPerPage={rowsPerPage}
												onPageChange={handleChangePage}
												rowsPerPageOptions={[5, 10, 25]}
												onRowsPerPageChange={handleChangeRowsPerPage}
											/>
										</>
									)}
								</>
							)}
						</>
					)}
				</DialogContent>
			</BootstrapDialog>
		</>
	);
};

export default ViewAndSearchAddRequestSendOut;

ViewAndSearchAddRequestSendOut.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
	circleId: PropTypes.string,
};
